import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppState } from "../store";
import { onDelete, onGet, onPatch, onPost } from "../../core/api-service";
import { ErrorState, ResponseState } from "../common-state";
import paths from "../../navigation/paths";
import { setErrorState } from "./common-slice";
import { UserResponse } from "./user-slice";

export enum CourseStatus {
  Active = "active",
  Inactive = "inactive",
  Draft = "draft",
}

export enum CourseLanguage {
  English = "english",
  Vietnamese = "vietnamese",
  Other = "other",
}

export enum CourseLevel {
  Beginner = "beginner",
  Intermediate = "intermediate",
  Advanced = "advanced",
}

export interface CourseCreateRequest {
  name: string;
  description: string;
  prerequisite?: string;
  syllabus: string;
  duration: number;
  schedule: string[];
  capacity: number;
  status: CourseStatus;
  language: CourseLanguage;
  level: CourseLevel;
  authorIds: string[];
}

export interface CourseUpdateRequest {
  name?: string;
  description?: string;
  prerequisite?: string;
  syllabus?: string;
  duration?: number;
  schedule?: string[];
  capacity?: number;
  status?: CourseStatus;
  language?: CourseLanguage;
  level?: CourseLevel;
  authorIds?: string[];
}

export interface GetAllCoursesRequest {
  status?: CourseStatus[];
  language?: CourseLanguage[];
  level?: CourseLevel[];
  limit?: number;
  offset?: number;
}

export interface CreateAttendance {
  courseId: string;
  userId: string;
  date: string;
  attended: boolean;
}

export interface AttendanceCreateRequest {
  attendances: CreateAttendance[];
}

export interface AttendanceResponse {
  id: string;
  course: CourseResponse;
  user: UserResponse;
  date: string;
  attended: boolean;
  createdAt: Date;
}

export interface CourseResponse {
  id: string;
  name: string;
  description: string;
  syllabus: string;
  pictureUrl?: string;
  prerequisite?: string;
  duration: number;
  schedule: string[];
  capacity: number;
  availability: number;
  status: CourseStatus;
  language: CourseLanguage;
  level: CourseLevel;
  createdAt: Date;
  updatedAt: Date;
  registeredUsers: UserResponse[];
  authors: UserResponse[];
}

export interface CourseState {
  courseByIdState: { data: CourseResponse | null; loading: boolean };
  allCoursesState: { data: CourseResponse[]; total: number; loading: boolean };
  createCourseState: {
    data: ResponseState<CourseResponse> | null;
    loading: boolean;
  };
  updateCourseState: {
    data: ResponseState<CourseResponse> | null;
    loading: boolean;
  };
  toggleCourseStatusState: {
    data: ResponseState<void> | null;
    loading: boolean;
  };
  deleteCourseState: { data: ResponseState<void> | null; loading: boolean };
  registerForCourseState: {
    data: ResponseState<void> | null;
    loading: boolean;
  };
  attendanceByCourseState: { data: AttendanceResponse[]; loading: boolean };
  markAttendanceState: {
    data: ResponseState<AttendanceResponse[]> | null;
    loading: boolean;
  };
  unmarkAttendanceState: {
    data: ResponseState<void> | null;
    loading: boolean;
  };
}

export const courseByIdStateAction = createAsyncThunk(
  "courses/id",
  async (params: { id: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onGet(`/courses/${params.id}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const allCoursesStateAction = createAsyncThunk(
  "courses/all",
  async (params: GetAllCoursesRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/courses/all", params);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const createCourseStateAction = createAsyncThunk(
  "courses/create",
  async (
    params: { request: FormData; userId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost(
        "/courses",
        params.request,
        undefined,
        true
      );
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const updateCourseStateAction = createAsyncThunk(
  "courses/update",
  async (
    params: { id: string; request: FormData; userId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPatch(
        `/courses/${params.id}`,
        params.request,
        undefined,
        true
      );
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const toggleCourseStatusStateAction = createAsyncThunk(
  "courses/toggle-status",
  async (
    params: { id: string; status: CourseStatus },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPatch(`/courses/${params.id}/toggle-status`, {
        status: params.status,
      });
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const deleteCourseStateAction = createAsyncThunk(
  "courses/delete",
  async (params: { id: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onDelete(`/courses/${params.id}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const registerForCourseStateAction = createAsyncThunk(
  "courses/register",
  async (
    params: { id: string; userId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost(`/courses/${params.id}/register`, {});
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const attendanceByCourseStateAction = createAsyncThunk(
  "courses/attendance",
  async (params: { courseId: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onGet(`/attendances/course/${params.courseId}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const markAttendanceStateAction = createAsyncThunk(
  "courses/mark-attendance",
  async (
    params: { request: AttendanceCreateRequest },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/attendances", params.request);
      // Log only in development mode
      if (process.env.NODE_ENV === "development") {
        console.log("Mark attendance response:", response);
      }
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ??
          "Failed to mark attendance. Please try again.",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const unmarkAttendanceStateAction = createAsyncThunk(
  "courses/unmark-attendance",
  async (params: { attendanceId: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onDelete(`/attendances/${params.attendanceId}`);
      // Log only in development mode
      if (process.env.NODE_ENV === "development") {
        console.log("Unmark attendance response:", response);
      }
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ??
          "Failed to unmark attendance. Please try again.",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);
export const courseSlice = createSlice({
  name: "courses",
  initialState: {
    courseByIdState: { data: null, loading: false },
    allCoursesState: { data: [], total: 0, loading: false },
    createCourseState: { data: null, loading: false },
    updateCourseState: { data: null, loading: false },
    toggleCourseStatusState: { data: null, loading: false },
    deleteCourseState: { data: null, loading: false },
    registerForCourseState: { data: null, loading: false },
    attendanceByCourseState: { data: [], loading: false },
    markAttendanceState: { data: null, loading: false },
    unmarkAttendanceState: { data: null, loading: false },
  } as CourseState,
  reducers: {
    clearCourseStates(state, _: PayloadAction) {
      state.courseByIdState = { data: null, loading: false };
      state.allCoursesState = { data: [], total: 0, loading: false };
      state.createCourseState = { data: null, loading: false };
      state.updateCourseState = { data: null, loading: false };
      state.toggleCourseStatusState = { data: null, loading: false };
      state.deleteCourseState = { data: null, loading: false };
      state.registerForCourseState = { data: null, loading: false };
      state.attendanceByCourseState = { data: [], loading: false };
      state.markAttendanceState = { data: null, loading: false };
      state.unmarkAttendanceState = { data: null, loading: false };
    },
    clearCreateCourseState(state, _: PayloadAction) {
      state.createCourseState = { data: null, loading: false };
    },
    clearUpdateCourseState(state, _: PayloadAction) {
      state.updateCourseState = { data: null, loading: false };
    },
    clearToggleCourseStatusState(state, _: PayloadAction) {
      state.toggleCourseStatusState = { data: null, loading: false };
    },
    clearDeleteCourseState(state, _: PayloadAction) {
      state.deleteCourseState = { data: null, loading: false };
    },
    clearRegisterForCourseState(state, _: PayloadAction) {
      state.registerForCourseState = { data: null, loading: false };
    },
    clearAttendanceByCourseState(state, _: PayloadAction) {
      state.attendanceByCourseState = { data: [], loading: false };
    },
    clearMarkAttendanceState(state, _: PayloadAction) {
      state.markAttendanceState = { data: null, loading: false };
    },
    clearUnmarkAttendanceState(state, _: PayloadAction) {
      state.unmarkAttendanceState = { data: null, loading: false };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(courseByIdStateAction.pending, (state: CourseState, _) => {
        state.courseByIdState.loading = true;
        state.courseByIdState.data = null;
      })
      .addCase(
        courseByIdStateAction.fulfilled,
        (state: CourseState, action) => {
          state.courseByIdState.loading = false;
          state.courseByIdState.data = action.payload.data as CourseResponse;
        }
      )
      .addCase(courseByIdStateAction.rejected, (state: CourseState, _) => {
        state.courseByIdState.loading = false;
      })
      .addCase(allCoursesStateAction.pending, (state: CourseState, _) => {
        state.allCoursesState.loading = true;
      })
      .addCase(
        allCoursesStateAction.fulfilled,
        (state: CourseState, action) => {
          state.allCoursesState.loading = false;
          state.allCoursesState.data = action.payload.data as CourseResponse[];
          state.allCoursesState.total = action.payload.meta.total as number;
        }
      )
      .addCase(allCoursesStateAction.rejected, (state: CourseState, _) => {
        state.allCoursesState.loading = false;
      })
      .addCase(createCourseStateAction.pending, (state: CourseState, _) => {
        state.createCourseState.loading = true;
        state.createCourseState.data = null;
      })
      .addCase(
        createCourseStateAction.fulfilled,
        (state: CourseState, action) => {
          state.createCourseState.loading = false;
          state.createCourseState.data =
            action.payload as ResponseState<CourseResponse>;
        }
      )
      .addCase(createCourseStateAction.rejected, (state: CourseState, _) => {
        state.createCourseState.loading = false;
      })
      .addCase(updateCourseStateAction.pending, (state: CourseState, _) => {
        state.updateCourseState.loading = true;
        state.updateCourseState.data = null;
      })
      .addCase(
        updateCourseStateAction.fulfilled,
        (state: CourseState, action) => {
          state.updateCourseState.loading = false;
          state.updateCourseState.data =
            action.payload as ResponseState<CourseResponse>;
        }
      )
      .addCase(updateCourseStateAction.rejected, (state: CourseState, _) => {
        state.updateCourseState.loading = false;
      })
      .addCase(
        toggleCourseStatusStateAction.pending,
        (state: CourseState, _) => {
          state.toggleCourseStatusState.loading = true;
          state.toggleCourseStatusState.data = null;
        }
      )
      .addCase(
        toggleCourseStatusStateAction.fulfilled,
        (state: CourseState, action) => {
          state.toggleCourseStatusState.loading = false;
          state.toggleCourseStatusState.data =
            action.payload as ResponseState<void>;
        }
      )
      .addCase(
        toggleCourseStatusStateAction.rejected,
        (state: CourseState, _) => {
          state.toggleCourseStatusState.loading = false;
        }
      )
      .addCase(deleteCourseStateAction.pending, (state: CourseState, _) => {
        state.deleteCourseState.loading = true;
        state.deleteCourseState.data = null;
      })
      .addCase(
        deleteCourseStateAction.fulfilled,
        (state: CourseState, action) => {
          state.deleteCourseState.loading = false;
          state.deleteCourseState.data = action.payload as ResponseState<void>;
        }
      )
      .addCase(deleteCourseStateAction.rejected, (state: CourseState, _) => {
        state.deleteCourseState.loading = false;
      })
      .addCase(
        registerForCourseStateAction.pending,
        (state: CourseState, _) => {
          state.registerForCourseState.loading = true;
          state.registerForCourseState.data = null;
        }
      )
      .addCase(
        registerForCourseStateAction.fulfilled,
        (state: CourseState, action) => {
          state.registerForCourseState.loading = false;
          state.registerForCourseState.data =
            action.payload as ResponseState<void>;
        }
      )
      .addCase(
        registerForCourseStateAction.rejected,
        (state: CourseState, _) => {
          state.registerForCourseState.loading = false;
        }
      )
      .addCase(
        attendanceByCourseStateAction.pending,
        (state: CourseState, _) => {
          state.attendanceByCourseState.loading = true;
          state.attendanceByCourseState.data = [];
        }
      )
      .addCase(
        attendanceByCourseStateAction.fulfilled,
        (state: CourseState, action) => {
          state.attendanceByCourseState.loading = false;
          state.attendanceByCourseState.data = action.payload
            .data as AttendanceResponse[];
        }
      )
      .addCase(
        attendanceByCourseStateAction.rejected,
        (state: CourseState, _) => {
          state.attendanceByCourseState.loading = false;
        }
      )
      .addCase(markAttendanceStateAction.pending, (state: CourseState, _) => {
        state.markAttendanceState.loading = true;
        state.markAttendanceState.data = null;
      })
      .addCase(
        markAttendanceStateAction.fulfilled,
        (state: CourseState, action) => {
          state.markAttendanceState.loading = false;
          state.markAttendanceState.data = action.payload as ResponseState<
            AttendanceResponse[]
          >;
        }
      )
      .addCase(markAttendanceStateAction.rejected, (state: CourseState, _) => {
        state.markAttendanceState.loading = false;
      })
      .addCase(unmarkAttendanceStateAction.pending, (state: CourseState, _) => {
        state.unmarkAttendanceState.loading = true;
        state.unmarkAttendanceState.data = null;
      })
      .addCase(
        unmarkAttendanceStateAction.fulfilled,
        (state: CourseState, action) => {
          state.unmarkAttendanceState.loading = false;
          state.unmarkAttendanceState.data =
            action.payload as ResponseState<void>;
        }
      )
      .addCase(
        unmarkAttendanceStateAction.rejected,
        (state: CourseState, _) => {
          state.unmarkAttendanceState.loading = false;
        }
      );
  },
});

export const {
  clearCourseStates,
  clearCreateCourseState,
  clearUpdateCourseState,
  clearToggleCourseStatusState,
  clearDeleteCourseState,
  clearRegisterForCourseState,
  clearAttendanceByCourseState,
  clearMarkAttendanceState,
  clearUnmarkAttendanceState,
} = courseSlice.actions;

export const getCourseByIdState = (state: AppState) =>
  state.courses.courseByIdState;
export const getAllCoursesState = (state: AppState) =>
  state.courses.allCoursesState;
export const getCreateCourseState = (state: AppState) =>
  state.courses.createCourseState;
export const getUpdateCourseState = (state: AppState) =>
  state.courses.updateCourseState;
export const getToggleCourseStatusState = (state: AppState) =>
  state.courses.toggleCourseStatusState;
export const getDeleteCourseState = (state: AppState) =>
  state.courses.deleteCourseState;
export const getRegisterForCourseState = (state: AppState) =>
  state.courses.registerForCourseState;
export const getAttendanceByCourseState = (state: AppState) =>
  state.courses.attendanceByCourseState;
export const getMarkAttendanceState = (state: AppState) =>
  state.courses.markAttendanceState;
export const getUnmarkAttendanceState = (state: AppState) =>
  state.courses.unmarkAttendanceState;

export default courseSlice.reducer;
